// Monthly build race (Oct 2, 2026) — front and center on the home dashboard all
// month. Each person's builds this month against the goal set at the first
// Monday's 1-on-1, with a pace line (share of the month's weekdays started).
// A build = a build ON THE CALENDAR this month (first build day in the month),
// via crew_calendar_builds; split deals count half. Oct 27, 2026 — was the crew
// `build` credit, which landed a build on the 1st in the month before.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  businessDays, fetchMonthBuildGoals, fetchMonthBuilds, monthName, monthStartOf, todayET,
} from "../../lib/dailyCommitments";

interface Racer { id: string; name: string; builds: number; goal: number | null }

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const VISIBLE = 6;

export default function MonthBuildRace({ meId, showDailyLink }: { meId: string; showDailyLink?: boolean }) {
  const today = todayET();
  const monthStart = monthStartOf(today);
  const [racers, setRacers] = useState<Racer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [roster, goals, builds] = await Promise.all([
          supabase.from("team_roster").select("id, full_name, role, is_active, on_leave")
            .in("role", ["restore_manager", "team_lead", "asm"]),
          fetchMonthBuildGoals(monthStart),
          fetchMonthBuilds(monthStart),
        ]);
        if (roster.error) throw roster.error;
        if (cancelled) return;
        const out: Racer[] = [];
        for (const p of roster.data ?? []) {
          const goal = goals[p.id] ?? null;
          const b = builds[p.id] ?? 0;
          // Inactive / on-leave members only stay on the board if they built this month.
          if ((!p.is_active || p.on_leave) && b === 0) continue;
          out.push({ id: p.id, name: p.full_name as string, builds: b, goal });
        }
        setRacers(out);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message ?? "Couldn't load the build race.");
      }
    })();
    return () => { cancelled = true; };
  }, [monthStart, tick]);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const bump = () => { if (t) clearTimeout(t); t = setTimeout(() => setTick((n) => n + 1), 2000); };
    const ch = supabase.channel("month-build-race")
      .on("postgres_changes", { event: "*", schema: "public", table: "crew_activity_log", filter: "kind=eq.build" }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "crew_goals" }, bump)
      .subscribe();
    return () => { if (t) clearTimeout(t); void supabase.removeChannel(ch); };
  }, []);

  const { total, elapsed } = businessDays(monthStart, today);
  const pace = total ? elapsed / total : 0;

  const sorted = useMemo(() => {
    if (!racers) return [];
    return [...racers].sort((a, b) => {
      const pa = a.goal ? a.builds / a.goal : -1, pb = b.goal ? b.builds / b.goal : -1;
      if (pa !== pb) return pb - pa;
      return b.builds - a.builds || a.name.localeCompare(b.name);
    });
  }, [racers]);

  const teamBuilds = (racers ?? []).reduce((s, r) => s + r.builds, 0);
  const teamGoal = (racers ?? []).reduce((s, r) => s + (r.goal ?? 0), 0);
  const anyGoals = teamGoal > 0;
  const leader = sorted.find((r) => r.goal && r.builds > 0);

  // Phones: top rows + always your own row.
  const shown = all ? sorted : (() => {
    const top = sorted.slice(0, VISIBLE);
    const me = sorted.find((r) => r.id === meId);
    return me && !top.includes(me) ? [...top, me] : top;
  })();

  return (
    <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label={`${monthName(monthStart)} build race`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-bardin-orange">🏁 {monthName(monthStart)} build race</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-black tabular-nums text-bardin-ink">{fmt(teamBuilds)}</span>
            <span className="text-sm font-semibold text-slate-500">{anyGoals ? `/ ${fmt(teamGoal)} team goal` : "builds this month"}</span>
          </div>
          <div className="text-[11px] text-slate-500">Day {elapsed} of {total} weekdays{leader ? ` · ${leader.name.split(" ")[0]} leads` : ""}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {showDailyLink && (
            <Link to="/canvas/manager-audit?mode=today" className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:opacity-90">✅ Confirm today's plans</Link>
          )}
          <div className="flex gap-3 text-[10px] text-slate-500">
            <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-bardin-orange align-middle" />Builds</span>
            <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-500 align-middle" />Goal hit</span>
            <span><i className="mr-1 inline-block h-2.5 w-0.5 bg-slate-700 align-middle" />Pace today</span>
          </div>
        </div>
      </div>

      {err ? (
        <div className="mt-3 rounded-md bg-rose-50 p-2 text-xs text-rose-700">{err}</div>
      ) : racers == null ? (
        <div className="mt-3 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-6 animate-pulse rounded bg-slate-100" />)}</div>
      ) : (
        <>
          {!anyGoals && (
            <div className="mt-3 rounded-md bg-orange-50 px-3 py-2 text-xs text-orange-800">
              No build goals set for {monthName(monthStart)} yet — they're set at the first-Monday 1-on-1. Builds so far are counting.
            </div>
          )}
          <ul className="mt-3 space-y-2">
            {shown.map((r) => {
              const pct = r.goal ? Math.min(100, (r.builds / r.goal) * 100) : 0;
              const hit = !!r.goal && r.builds >= r.goal;
              const onPace = !!r.goal && r.builds / r.goal >= pace;
              const mine = r.id === meId;
              return (
                <li key={r.id} className={`grid grid-cols-[minmax(84px,130px)_1fr_auto] items-center gap-2 rounded-md px-1 ${mine ? "bg-orange-50 ring-1 ring-orange-200" : ""}`}>
                  <span className={`truncate text-[13px] ${mine ? "font-bold text-bardin-ink" : "font-semibold text-slate-700"}`}>{mine ? "You" : r.name}</span>
                  <span className="relative h-5 rounded-md border border-slate-200 bg-slate-50">
                    {r.goal ? (
                      <>
                        <span className={`absolute inset-y-0 left-0 rounded-[5px] ${hit ? "bg-emerald-500" : "bg-bardin-orange"}`} style={{ width: `${pct}%` }} />
                        <span className="absolute -inset-y-1 w-0.5 bg-slate-700/60" style={{ left: `${pace * 100}%` }} />
                      </>
                    ) : (
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] text-slate-400">no goal set</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 text-[12px] tabular-nums text-slate-700">
                    <b>{fmt(r.builds)}</b>{r.goal ? <span className="text-slate-400">/ {fmt(r.goal)}</span> : null}
                    {r.goal ? (
                      <span className={`rounded-full px-1.5 text-[10px] font-bold ${hit ? "bg-emerald-100 text-emerald-700" : onPace ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {hit ? "✓ hit" : onPace ? "on pace" : "behind"}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {sorted.length > shown.length || all ? (
            <button onClick={() => setAll((a) => !a)} className="mt-2 text-xs font-semibold text-bardin-orange">
              {all ? "Show fewer" : `Show all ${sorted.length}`}
            </button>
          ) : null}
          <div className="mt-2 text-[10px] text-slate-400">A build counts when Chad approves it and it's on the calendar. Split deals count half.</div>
        </>
      )}
    </section>
  );
}
